import {useCallback, useEffect, useMemo, useState} from 'react';
import {useChat} from './ChatProvider';
import ConversationModal from './ConversationModal';
import useConversationTimeline from './hooks/useConversationTimeline';
import useResizableChatLayout from './hooks/useResizableChatLayout';
import ChatSidebar from './ui/ChatSidebar';
import ConversationPane from './ui/ConversationPane';
import ResizeHandle from './ui/ResizeHandle';
import ThreadPanel from './ui/ThreadPanel';
import './Chat.css';

function preferredConversation(conversations) {
    return conversations.find(item => (item.unread_root_count || 0) + (item.unread_thread_count || 0) > 0)
        || conversations.find(item => item.kind === 'CHANNEL')
        || conversations[0]
        || null;
}

/**
 * Router-independent Chat surface for full pages, modals, drawers, and
 * entity-specific panels. It consumes the application-level ChatProvider.
 */
export default function ChatWorkspace({
    conversationId,
    defaultConversationId = 0,
    onConversationChange,
    showSidebar = true,
    allowCreate = showSidebar,
    autoSelect = showSidebar,
    variant = 'page',
    className = '',
    style,
    renderHeaderActions,
    renderMessageActions,
}) {
    const chat = useChat();
    const controlled = conversationId !== undefined;
    const [internalConversationId, setInternalConversationId] = useState(defaultConversationId);
    const [createOpen, setCreateOpen] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [threadRoot, setThreadRoot] = useState(null);
    const activeConversationId = Number(controlled ? conversationId : internalConversationId) || 0;
    const conversation = chat.conversations.find(item => item.id === activeConversationId) || null;
    const {loadConversation, refreshConversations, setError, setActiveConversationId} = chat;

    const refresh = useCallback(async () => {
        if (refreshing) return;
        setRefreshing(true);
        try {
            await refreshConversations();
        } catch (refreshError) {
            setError?.(refreshError);
        } finally {
            setRefreshing(false);
        }
    }, [refreshConversations, refreshing, setError]);
    const invalidation = conversation ? chat.invalidations[conversation.id] || 0 : 0;
    const timeline = useConversationTimeline({
        conversation,
        currentUser: chat.currentUser,
        invalidation,
        refreshConversations,
        readConversationMessages: chat.readConversationMessages,
        cacheConversationMessages: chat.cacheConversationMessages,
    });
    const {refreshMessages} = timeline;
    const hasThread = Boolean(threadRoot && conversation);
    const layout = useResizableChatLayout({hasThread, hasSidebar: showSidebar});

    const selectConversation = useCallback((nextConversation, source = 'user') => {
        const nextId = nextConversation?.id || 0;
        if (!controlled) setInternalConversationId(nextId);
        setSidebarOpen(false);
        setThreadRoot(null);
        onConversationChange?.(nextConversation, {source});
    }, [controlled, onConversationChange]);

    useEffect(() => {
        if (!autoSelect || conversation || chat.conversations.length === 0) return;
        selectConversation(preferredConversation(chat.conversations), 'auto');
    }, [autoSelect, conversation, chat.conversations, selectConversation]);

    useEffect(() => setThreadRoot(null), [activeConversationId]);

    useEffect(() => {
        if (!conversation || conversation.details_loaded) return undefined;
        let active = true;
        loadConversation(conversation.id).catch(loadError => {
            if (active) setError?.(loadError);
        });
        return () => {
            active = false;
        };
    }, [conversation, loadConversation, setError]);

    useEffect(() => {
        setActiveConversationId(conversation?.id || null);
        return () => setActiveConversationId(null);
    }, [setActiveConversationId, conversation?.id]);

    const handleThreadChanged = useCallback(() => Promise.all([
        refreshMessages(true),
        refreshConversations(),
    ]), [refreshMessages, refreshConversations]);

    const workspaceStyle = useMemo(() => ({...layout.style, ...style}), [layout.style, style]);
    const workspaceClassName = [
        'chat-page',
        `is-${variant}`,
        hasThread ? 'has-thread' : '',
        showSidebar ? '' : 'without-sidebar',
        className,
    ].filter(Boolean).join(' ');

    return (
        <div className={workspaceClassName} ref={layout.containerRef} style={workspaceStyle}>
            {showSidebar ? (
                <>
                    <ChatSidebar
                        conversations={chat.conversations}
                        currentUserId={chat.currentUser?.id}
                        selectedId={conversation?.id}
                        onSelect={selectConversation}
                        onCreate={allowCreate ? () => setCreateOpen(true) : null}
                        onRefresh={refresh}
                        onLoadMore={chat.loadMoreConversations}
                        hasMore={chat.hasMoreConversations}
                        onSearch={chat.searchConversations}
                        conversationGroups={chat.conversationGroups}
                        onLoadGroups={chat.loadConversationGroups}
                        refreshing={refreshing}
                        open={sidebarOpen}
                        onClose={() => setSidebarOpen(false)}
                    />
                    <ResizeHandle
                        panel="sidebar"
                        onPointerDown={layout.startResize}
                        onKeyDown={layout.resizeWithKeyboard}
                        onReset={layout.resetWidth}
                    />
                </>
            ) : null}
            <ConversationPane
                conversation={conversation}
                currentUser={chat.currentUser}
                connectionStatus={chat.connectionStatus}
                showSidebar={showSidebar}
                onOpenSidebar={() => setSidebarOpen(true)}
                onCreate={allowCreate ? () => setCreateOpen(true) : null}
                onOpenThread={setThreadRoot}
                timeline={timeline}
                globalError={chat.error}
                renderHeaderActions={renderHeaderActions}
                renderMessageActions={renderMessageActions}
            />
            {hasThread ? (
                <>
                    <ResizeHandle
                        panel="thread"
                        onPointerDown={layout.startResize}
                        onKeyDown={layout.resizeWithKeyboard}
                        onReset={layout.resetWidth}
                    />
                    <ThreadPanel
                        conversation={conversation}
                        root={timeline.messages.find(message => message.id === threadRoot.id) || threadRoot}
                        currentUser={chat.currentUser}
                        invalidation={invalidation}
                        onClose={() => setThreadRoot(null)}
                        onRootChanged={handleThreadChanged}
                        renderMessageActions={renderMessageActions}
                    />
                </>
            ) : null}
            {allowCreate ? (
                <ConversationModal
                    show={createOpen}
                    onHide={() => setCreateOpen(false)}
                    chat={chat}
                    onCreated={created => selectConversation(created, 'created')}
                />
            ) : null}
        </div>
    );
}
